// External Collection → Notifications — staff compose screen. Pick an
// audience (Individual / Category / Everyone), write a plain-text title +
// body, optionally email recipients, and send. A live recipient count
// (via the preview endpoint) shows the blast size before sending; a
// confirm guards large/Everyone sends.

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Send } from "lucide-react";
import { Badge, Button, Input } from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { useToast } from "@/components/ui/Toast";
import {
  adminNotificationsApi,
  type NotificationAudienceType,
} from "@/api/adminNotifications";
import { portalAccountsApi } from "@/api/portalAccounts";
import type { MaterialAudience } from "@/api/portalMaterials";

const CATEGORIES: MaterialAudience[] = ["Broker", "Buyer", "Seller", "Vendor"];

// Anything at or above this resolved count gets an explicit confirm.
const CONFIRM_THRESHOLD = 10;

export function NotificationComposePage() {
  useBreadcrumbs([
    { label: "External Collection" },
    { label: "Notifications", href: "/admin/notifications" },
    { label: "New" },
  ]);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();

  const [audienceType, setAudienceType] = useState<NotificationAudienceType>("Category");
  const [categories, setCategories] = useState<MaterialAudience[]>([]);
  const [accountIds, setAccountIds] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [sendEmail, setSendEmail] = useState(true);

  // Account search for the Individual picker.
  const accountSearch = useQuery({
    queryKey: ["notification-account-search", search],
    queryFn: () => portalAccountsApi.list({ search: search || undefined, limit: 20 }),
    enabled: audienceType === "Individual",
  });
  const accountResults = accountSearch.data?.items ?? [];

  // Names of already-selected accounts, kept so chips render even when the
  // search box is cleared. Seeded from each search result we've seen.
  const [selectedNames, setSelectedNames] = useState<Record<string, string>>({});

  // Live recipient count. Only meaningful once the audience is fully
  // specified (a category chosen, an account chosen, or Everyone).
  const previewReady =
    audienceType === "Everyone" ||
    (audienceType === "Category" && categories.length > 0) ||
    (audienceType === "Individual" && accountIds.length > 0);

  const preview = useQuery({
    queryKey: ["notification-preview", audienceType, categories, accountIds],
    queryFn: () =>
      adminNotificationsApi.recipientPreview({ audienceType, categories, accountIds }),
    enabled: previewReady,
  });
  const recipientCount = preview.data?.count ?? null;

  const create = useMutation({
    mutationFn: () =>
      adminNotificationsApi.create({
        title: title.trim(),
        body,
        audienceType,
        categories: audienceType === "Category" ? categories : undefined,
        accountIds: audienceType === "Individual" ? accountIds : undefined,
        sendEmail,
      }),
    onSuccess: (dto) => {
      toast({
        tone: "success",
        message: `Notification sent to ${dto.readStats.total} recipient(s).`,
      });
      void qc.invalidateQueries({ queryKey: ["admin-notifications"] });
      navigate("/admin/notifications");
    },
    onError: (e: Error) => toast({ tone: "error", message: e.message }),
  });

  const canSend =
    title.trim().length > 0 && body.trim().length > 0 && previewReady && !create.isPending;

  const audienceLabel = useMemo(() => {
    if (audienceType === "Everyone") return "everyone";
    if (audienceType === "Category")
      return categories.length ? categories.map((c) => `${c}s`).join(", ") : "the selected categories";
    return "the selected accounts";
  }, [audienceType, categories]);

  function toggleCategory(c: MaterialAudience) {
    setCategories((prev) =>
      prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c],
    );
  }

  function toggleAccount(id: string, name: string) {
    setAccountIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
    setSelectedNames((prev) => ({ ...prev, [id]: name }));
  }

  function onSend() {
    const count = recipientCount ?? 0;
    if (count === 0) {
      toast({ tone: "error", message: "This audience resolves to 0 recipients." });
      return;
    }
    if (
      (audienceType === "Everyone" || count >= CONFIRM_THRESHOLD) &&
      !confirm(`Send to ${count} ${audienceLabel}?`)
    ) {
      return;
    }
    create.mutate();
  }

  return (
    <div className="flex flex-col gap-space-4 max-w-2xl">
      <PageHeader
        title="New notification"
        description="Compose a plain-text notification for external portal users."
      />

      {/* Audience picker */}
      <fieldset className="flex flex-col gap-space-3">
        <legend className="text-small font-semibold text-onyx mb-space-1">Audience</legend>
        <div className="flex flex-col gap-space-2">
          {(
            [
              ["Individual", "Specific account(s)"],
              ["Category", "A category (Brokers / Buyers / Sellers / Vendors)"],
              ["Everyone", "Everyone (all active external accounts)"],
            ] as Array<[NotificationAudienceType, string]>
          ).map(([value, label]) => (
            <label key={value} className="flex items-center gap-space-2 text-small text-onyx">
              <input
                type="radio"
                name="audienceType"
                value={value}
                checked={audienceType === value}
                onChange={() => setAudienceType(value)}
              />
              {label}
            </label>
          ))}
        </div>

        {audienceType === "Category" && (
          <div className="flex flex-wrap gap-space-3 pl-space-4">
            {CATEGORIES.map((c) => (
              <label key={c} className="flex items-center gap-space-2 text-small text-onyx">
                <input
                  type="checkbox"
                  checked={categories.includes(c)}
                  onChange={() => toggleCategory(c)}
                />
                {c}
              </label>
            ))}
          </div>
        )}

        {audienceType === "Individual" && (
          <div className="flex flex-col gap-space-2 pl-space-4">
            <Input
              type="search"
              placeholder="Search accounts by name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search external accounts"
            />
            {accountIds.length > 0 && (
              <div className="flex flex-wrap gap-space-2">
                {accountIds.map((id) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => toggleAccount(id, selectedNames[id] ?? "")}
                    className="inline-flex items-center gap-space-1 rounded-pill bg-gold/15 text-onyx px-space-2 py-[2px] text-caption"
                  >
                    {selectedNames[id] ?? id} ✕
                  </button>
                ))}
              </div>
            )}
            <ul className="max-h-48 overflow-y-auto border border-border-light rounded-md divide-y divide-border-light">
              {accountSearch.isLoading && (
                <li className="px-space-3 py-space-2 text-caption text-gray-mid">Searching…</li>
              )}
              {!accountSearch.isLoading && accountResults.length === 0 && (
                <li className="px-space-3 py-space-2 text-caption text-gray-mid">No accounts found.</li>
              )}
              {accountResults.map((a) => (
                <li key={a.id}>
                  <label className="flex items-center gap-space-2 px-space-3 py-space-2 text-small text-onyx cursor-pointer hover:bg-white-warm">
                    <input
                      type="checkbox"
                      checked={accountIds.includes(a.id)}
                      onChange={() => toggleAccount(a.id, a.fullName)}
                    />
                    <span className="flex-1">
                      {a.fullName}{" "}
                      <span className="text-caption text-gray-mid">{a.email}</span>
                    </span>
                    <Badge status="info">{a.accountType}</Badge>
                  </label>
                </li>
              ))}
            </ul>
          </div>
        )}
      </fieldset>

      {/* Live recipient count */}
      <div className="text-small text-onyx" aria-live="polite">
        {previewReady ? (
          preview.isLoading ? (
            "Calculating recipients…"
          ) : (
            <span className="font-semibold">
              Will be sent to {recipientCount ?? 0} recipient(s).
            </span>
          )
        ) : (
          <span className="text-gray-mid">Choose an audience to see the recipient count.</span>
        )}
      </div>

      {/* Title + Body */}
      <label className="flex flex-col gap-space-1">
        <span className="text-small font-semibold text-onyx">Title</span>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={200}
          placeholder="Announcement title"
        />
      </label>

      <label className="flex flex-col gap-space-1">
        <span className="text-small font-semibold text-onyx">Message</span>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={8}
          placeholder="Write your message. Line breaks are preserved."
          className="w-full rounded-md border border-border-light px-space-3 py-space-2 text-small text-onyx focus:outline-none focus:ring-2 focus:ring-gold"
        />
      </label>

      {/* Email toggle */}
      <label className="flex items-start gap-space-2 text-small text-onyx">
        <input
          type="checkbox"
          checked={sendEmail}
          onChange={(e) => setSendEmail(e.target.checked)}
          className="mt-[3px]"
        />
        <span>
          Also email recipients
          <span className="block text-caption text-gray-mid">
            Sends a brief "You have a new notification from Phoenix Capital" email with a
            link to the portal login page. The message content lives in the portal.
          </span>
        </span>
      </label>

      <div className="flex items-center gap-space-3">
        <Button type="button" onClick={onSend} disabled={!canSend} loading={create.isPending}>
          <Send size={16} className="mr-space-1" /> Send notification
        </Button>
        <Button type="button" variant="secondary" onClick={() => navigate("/admin/notifications")}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
